import React from 'react';
import LoadingIndicator from '../../components/shared/LoadingIndicator/LoadingIndicator';
import { Navigate } from 'react-router-dom';
import { mapReportSectionsToSectionData } from './utils';
import { AnalysisReportSections, SavedAnalysisReport } from '../types';
import useFetchSavedReportWithCompetitors from '../hooks/useFetchSavedReportWithCompetitors';
import { CompetitorsResults } from './types';
import AnalysisReport from './AnalysisReport';
import { useFetchAnalysisStructureQuery } from '../service';
import { getFlatSections } from '../utils';

type Props = {
    guid: string;
    websiteUrl: string;
};

function groupCompetitorsBySections(
    competitorReports: Record<string, SavedAnalysisReport>,
    sections: AnalysisReportSections
): Record<string, CompetitorsResults> {
    return Object.keys(sections).reduce((record, sectionKey) => {
        const groupedResults = Object.keys(competitorReports).reduce(
            (object, competitor) => ({ ...object, [competitor]: competitorReports[competitor].sections[sectionKey].result }),
            {}
        );

        return {
            ...record,
            [sectionKey]: groupedResults,
        };
    }, {});
}

export default function ArchivedAnalysisReport({ guid, websiteUrl }: Props) {
    const {
        savedAnalysisReport,
        competitorReports,
        isLoading: isLoadingAnalysisReport,
        isError: isFetchReportError,
    } = useFetchSavedReportWithCompetitors(guid, websiteUrl);

    const { data: analysisStructure, isLoading: isLoadingAnalysisStructure } = useFetchAnalysisStructureQuery({ fields: ['key', 'title', 'relevance'] });

    const mainUrlAnalysisSections = !!savedAnalysisReport ? Object.keys(savedAnalysisReport.sections) : [];

    const competitorOverviews = Object.keys(competitorReports).reduce(
        (object, competitor) => ({
            ...object,
            [competitor]: competitorReports[competitor].overview,
        }),
        {}
    );

    /**
     * Grouped competitors by the sections of their reports, in the form of
     * [section-key]: {
     *                  [competitor]: result
     *                }
     */
    const groupedCompetitorsSections = !!savedAnalysisReport ? groupCompetitorsBySections(competitorReports, savedAnalysisReport.sections) : undefined;
    const isFullReport = !!analysisStructure && getFlatSections(analysisStructure).length === mainUrlAnalysisSections.length;

    const isLoading = isLoadingAnalysisReport || isLoadingAnalysisStructure;
    const showReport = savedAnalysisReport && analysisStructure && !isLoading && !isFetchReportError;

    return (
        <>
            {isLoading && (
                <LoadingIndicator
                    detailed
                    text="Loading results"
                />
            )}

            {isFetchReportError && <Navigate to="/analysis" />}

            {showReport && (
                <AnalysisReport
                    guid={guid}
                    url={websiteUrl}
                    structure={analysisStructure}
                    scoreData={savedAnalysisReport.overview}
                    competitorsScoreData={competitorOverviews}
                    totalSectionsCount={mainUrlAnalysisSections.length}
                    sectionResults={mapReportSectionsToSectionData(savedAnalysisReport.sections)}
                    competitorSectionResults={groupedCompetitorsSections}
                    isFullReport={isFullReport}
                    createdAt={savedAnalysisReport.created_at}
                />
            )}
        </>
    );
}
